import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { RootState } from '../../../app.store';

import type {
  FilterScopeState,
  FiltersState,
  InitScopePayload,
  SetFilterPayload,
  SetKeywordPayload,
  SetPaginationPayload,
} from './filters.type';

const initialState: FiltersState = {
  scopes: {},
};

const filtersSlice = createSlice({
  name: 'filters',
  initialState,
  reducers: {
    initScope: <T>(
      state: FiltersState,
      action: PayloadAction<InitScopePayload<T>>,
    ) => {
      const {
        scope,
        defaultPage = 1,
        defaultLimit = 10,
        defaultFilters = {} as T,
      } = action.payload;

      if (!state.scopes[scope]) {
        state.scopes[scope] = {
          keyword: '',
          page: defaultPage,
          limit: defaultLimit,
          filters: defaultFilters,
        } as FilterScopeState<T>;
      }
    },

    setPagination: (state, action: PayloadAction<SetPaginationPayload>) => {
      const { scope, page, limit } = action.payload;

      if (state.scopes[scope]) {
        state.scopes[scope].page = page;
        state.scopes[scope].limit = limit;
      }
    },

    setKeyword: (state, action: PayloadAction<SetKeywordPayload>) => {
      const { scope, keyword, defaultPage = 1 } = action.payload;

      if (state.scopes[scope]) {
        state.scopes[scope].keyword = keyword;
        state.scopes[scope].page = defaultPage;
      }
    },

    setFilter: (
      state: FiltersState,
      action: PayloadAction<SetFilterPayload>,
    ) => {
      const { scope, key, value, defaultPage = 1 } = action.payload;

      if (state.scopes[scope]) {
        state.scopes[scope].filters[key] = value;
        state.scopes[scope].page = defaultPage;
      }
    },

    resetScope: <T>(
      state: FiltersState,
      action: PayloadAction<InitScopePayload<T>>,
    ) => {
      const {
        scope,
        defaultPage = 1,
        defaultLimit = 10,
        defaultFilters = {} as T,
      } = action.payload;

      if (state.scopes[scope]) {
        state.scopes[scope] = {
          keyword: '',
          page: defaultPage,
          limit: defaultLimit,
          filters: defaultFilters,
        } as FilterScopeState<T>;
      }
    },

    clearAllScopes: (state) => {
      state.scopes = {};
    },
  },
});

export const {
  initScope,
  setPagination,
  setKeyword,
  setFilter,
  resetScope,
  clearAllScopes,
} = filtersSlice.actions;

export const filtersReducer = filtersSlice.reducer;

// Select all filter scopes
export const selectFilters = (state: RootState) => state.filters;

// Select a specific filter scope
export const selectFilterScope = <T = Record<string, any>>(scope: string) => {
  return (state: RootState): FilterScopeState<T> | undefined => {
    return state.filters.scopes[scope] as FilterScopeState<T> | undefined;
  };
};

// Select pagination state for a specific scope
export const selectFilterPagination = (scope: string) => (state: RootState) => {
  const scopeState = state.filters.scopes[scope];
  return scopeState
    ? {
        page: scopeState.page,
        limit: scopeState.limit,
      }
    : { page: 1, limit: 10 };
};

// Select keyword for a specific scope
export const selectFilterKeyword = (scope: string) => (state: RootState) => {
  return state.filters.scopes[scope]?.keyword || '';
};

// Select filters object for a specific scope
export const selectFilterObject = <T = Record<string, any>>(scope: string) => {
  return (state: RootState): T => {
    return (state.filters.scopes[scope]?.filters as T) || ({} as T);
  };
};
